import dayjs, { Dayjs } from 'dayjs';

/**
 * 时长计算模块
 * 用于计算审批申请中的考勤时长
 */

/**
 * 时间段类型：上午或下午
 */
export type PeriodType = 'morning' | 'afternoon';

/**
 * 时长计算结果
 */
export interface DurationResult {
  /** 时长（秒） */
  duration: number;
  /** 半天数 */
  halfDays: number;
}

/**
 * 计算考勤时长
 * 根据企业微信文档，halfday类型的时间点：
 * - 开始时间：上午=00:00:00，下午=12:00:00
 * - 结束时间：上午=12:00:00（对应时间+12小时），下午=24:00:00即次日00:00:00（对应时间+12小时）
 *
 * @param startDate 开始日期
 * @param endDate 结束日期
 * @param startPeriod 开始时间点 (morning: 上午, afternoon: 下午)
 * @param endPeriod 结束时间点 (morning: 上午, afternoon: 下午)
 * @param excludeWeekends 是否排除周末（默认为false）
 * @returns {duration: 秒数, halfDays: 半天数}
 */
export const calculateDuration = (
  startDate: Dayjs | Date | string,
  endDate: Dayjs | Date | string,
  startPeriod: PeriodType,
  endPeriod: PeriodType,
  excludeWeekends: boolean = true,
): DurationResult => {
  // 设置开始时间点
  let start = dayjs(startDate);
  if (startPeriod === 'morning') {
    start = start.hour(0).minute(0).second(0); // 上午：00:00:00
  } else {
    // afternoon
    start = start.hour(12).minute(0).second(0); // 下午：12:00:00
  }

  // 设置结束时间点（需要+12小时）
  let end = dayjs(endDate);
  if (endPeriod === 'morning') {
    end = end.hour(0).minute(0).second(0).add(12, 'hour'); // 上午结束：00:00:00 + 12小时 = 12:00:00
  } else {
    // afternoon
    end = end.hour(12).minute(0).second(0).add(12, 'hour'); // 下午结束：12:00:00 + 12小时 = 次日00:00:00
  }

  // 如果不排除周末，按原有逻辑计算
  if (!excludeWeekends) {
    const durationSeconds = end.diff(start, 'second');
    const halfDays = Math.round(durationSeconds / 43200);
    return {
      duration: durationSeconds,
      halfDays,
    };
  }

  // 排除周末的计算逻辑
  let totalHalfDays = 0;
  let currentDate = dayjs(startDate);
  const endDateDay = dayjs(endDate);

  // 遍历每一天，统计工作日的半天数
  while (currentDate.isSame(endDateDay, 'day') || currentDate.isBefore(endDateDay, 'day')) {
    const dayOfWeek = currentDate.day(); // 0=周日, 6=周六

    // 跳过周末
    if (dayOfWeek !== 0 && dayOfWeek !== 6) {
      // 判断是否是第一天
      const isFirstDay = currentDate.isSame(dayjs(startDate), 'day');
      // 判断是否是最后一天
      const isLastDay = currentDate.isSame(endDateDay, 'day');

      if (isFirstDay && isLastDay) {
        // 同一天的情况
        if (startPeriod === 'morning' && endPeriod === 'afternoon') {
          totalHalfDays += 2; // 整天
        } else if (startPeriod === 'morning' && endPeriod === 'morning') {
          totalHalfDays += 1; // 只有上午
        } else if (startPeriod === 'afternoon' && endPeriod === 'afternoon') {
          totalHalfDays += 1; // 只有下午
        }
        // 如果是 afternoon -> morning，则不计入（跨越了中午但没有完整半天）
      } else if (isFirstDay) {
        // 第一天：根据开始时间段决定
        if (startPeriod === 'morning') {
          totalHalfDays += 2; // 上午+下午
        } else {
          totalHalfDays += 1; // 只有下午
        }
      } else if (isLastDay) {
        // 最后一天：根据结束时间段决定
        if (endPeriod === 'afternoon') {
          totalHalfDays += 2; // 上午+下午
        } else {
          totalHalfDays += 1; // 只有上午
        }
      } else {
        // 中间的完整工作日
        totalHalfDays += 2; // 整天
      }
    }

    currentDate = currentDate.add(1, 'day');
  }

  // 计算总时长（秒）= 半天数 * 43200秒
  const durationSeconds = totalHalfDays * 43200;

  return {
    duration: durationSeconds,
    halfDays: totalHalfDays,
  };
};

/**
 * 根据时间戳判断是上午还是下午
 * @param timestamp Unix时间戳（秒）
 * @returns '上午' 或 '下午'
 */
export const getPeriodFromTimestamp = (timestamp: number): string => {
  const time = dayjs.unix(timestamp);
  const hour = time.hour();
  // 0点开始为上午，12点开始为下午
  return hour < 12 ? '上午' : '下午';
};

/**
 * 根据时间戳获取时间段类型
 * @param timestamp Unix时间戳（秒）
 * @returns PeriodType
 */
export const getPeriodTypeFromTimestamp = (timestamp: number): PeriodType => {
  const time = dayjs.unix(timestamp);
  const hour = time.hour();
  return hour < 12 ? 'morning' : 'afternoon';
};

/**
 * 格式化时长为可读字符串
 * @param duration 时长（秒）
 * @returns 格式化后的字符串，如 "1天0.5天" 或 "8小时30分钟"
 */
export const formatDuration = (duration: number): string => {
  const hours = Math.floor(duration / 3600);
  const minutes = Math.floor((duration % 3600) / 60);
  const days = Math.floor(hours / 8); // 按8小时为一个工作日
  const remainingHours = hours % 8;

  if (days > 0) {
    return `${days}天${remainingHours > 0 ? `${remainingHours}小时` : ''}${minutes > 0 ? `${minutes}分钟` : ''}`;
  }
  return `${hours}小时${minutes > 0 ? `${minutes}分钟` : ''}`;
};

/**
 * 根据半天数格式化为天数显示
 * @param halfDays 半天数
 * @returns 格式化后的字符串，如 "1.5天"
 */
export const formatHalfDays = (halfDays: number): string => {
  const days = halfDays * 0.5;
  return `${days}天`;
};

